import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

/**
 * §9: セル背景の入れ子三項を cellBackground 関数に抽出。
 *
 * 優先度: locked > isOverContract > isEditing > zebra
 */
export type CellState = {
  locked: boolean
  /**
   * 「編集中（未保存の手動変更）」か (AC-S4D-049)。 `editState.isKomaEditing` の
   * 結果を渡す。 「変更後（保存済みの手動変更）」は背景を持たない別レイヤ
   * (design の `cell-manual` は `::after` の下線マーカーのみ、GridCell.tsx 側で
   * 描画する) なのでここには含めない。
   */
  isEditing: boolean
  isOverContract: boolean
  /**
   * 縞を敷く側の行か (= 表全体を通した行番号が奇数)。
   * 日付ブロック単位ではなく**行単位**に敷く (8080
   * `.table-48 tbody tr:nth-child(even) td`)。 2026-08-28 デザイナー指摘。
   */
  isZebraRow: boolean
}

export function cellBackground(state: CellState): string {
  if (state.locked) {
    return '#f0f2f5' // eclair surface-disabled
  }
  // 契約値超過は DS の warning 地色。 design `supply/48data.html:328-334` の
  // `td.over-contract` が `background: var(--surface-warning-low)` (#fffcf0)。
  // 旧実装の `#fffbe6` は DS のどの token でもない生 hex で、 コメントの
  // 「eclair ≈ surface-warning-low」 は誤記だった (実測: DS は #fffcf0)。
  if (state.isOverContract) {
    return LightModeSemanticColors.Surface.warningLow.value
  }
  // [#5125 / #8988 / AC-S4D-049] 編集中 (未保存の手動変更)。
  // design `supply/48data.html:211` の `td.cell-edited`:
  // `background: color-mix(in srgb, var(--surface-accent-primary) 16%, var(--surface-secondary))`。
  // 「変更後 (保存済み)」が地色を持たない下線マーカーで表される規則自体は
  // design に元々ある ([#4888 / AC-S4D-026]、48data.html:219 以降の
  // `cell-manual`)。 Des 確定 (issue #5125 PR #846) でセル状態 3 種別
  // (編集中/エラー/変更後) が明示的に区別されたことで、 旧実装が「編集中」
  // 「変更後」の両方を 1 つの地色 (旧 `#fffbe6`、DS に無い生 hex) に畳んでいた
  // 状態を解消し、 旧地色は「編集中」専用の accent 系配色 (cell-edited) に
  // 置き換わる (zebra の縞色を問わず常に同じ配色で塗る、design も zebra 条件を
  // 見ていない)。
  if (state.isEditing) {
    return `color-mix(in srgb, ${LightModeSemanticColors.Surface.accentPrimary.value} 16%, ${LightModeSemanticColors.Surface.secondary.value})`
  }
  // zebra stripe。 `Surface.secondary` (#f8f8f8) は 8080 の行背景の実測値
  // (rgb(248,248,248)) と一致するので token を使う (旧 '#f5f7f9' は別色だった)。
  return state.isZebraRow
    ? LightModeSemanticColors.Surface.secondary.value
    : LightModeSemanticColors.Surface.primary.value
}

// design token の raw hex 値 (eclair tokens) — 定数として export
export const COLOR_TEXT_MID = LightModeSemanticColors.Text.midEmphasis.value
export const COLOR_BORDER_LOW = LightModeSemanticColors.Border.lowEmphasis.value
export const COLOR_TEXT_HIGH = LightModeSemanticColors.Text.highEmphasis.value
export const COLOR_TEXT_LOW = LightModeSemanticColors.Text.lowEmphasis.value

/**
 * データ列 (leg ごとの sub 列ヘッダ th + データセル td) の明示幅。
 *
 * 元は design/supply/48data.html `.table-48 { --s48-data-col-w: 96px; }` 由来
 * (2026-07-31 #4343「列が少ないときにデータ列が極端に広くなる問題」の対策)。
 * 原因は 2 つあり、主因は後者:
 *   1. table 側の `min-width: 100%` — 列が少ないと余った幅を列が山分けして間延びする
 *   2. データセルの `<input>` の既定 intrinsic 幅 (size 未指定で ≒177px を申告する)
 * → データ列 (sub 列ヘッダ th + データ td) に本定数の明示幅を与えて 2 を潰し、
 * table の `min-width: 100%` を外して 1 を潰す (Supply48DataGrid.tsx)。
 * 余った幅はテーブル右側の余白として残る (= 表を引き伸ばさない・左詰め)。
 *
 * 値は AC-S4D-039a (issue #7057、2026-09-04 PdM 判断) で 7rem 固定と定められ、
 * 96px (6rem、旧 #4343 由来値) から 112px (7rem) へ更新した。取引先名の長短
 * (フルネーム→取引先マスタ略称) で列幅を揺らさないための固定値で、#4343 の
 * 「列が少ない時に間延びしない」目的とは独立して働く (本数値を変えても #4343 の
 * 対策そのものは崩れない)。design HTML の 96px は本 AC を未反映のまま (旧仕様の
 * 「取引先名（取引先コード）」併記 2 段見出しを表示しており、spec の「略称のみ・
 * コード併記なし」と食い違う。spec を優先する)。
 */
export const S48_DATA_COL_WIDTH_PX = 112

/** グリッド sticky 列の基本スタイル */
export const STICKY_COL_STYLES: React.CSSProperties = {
  position: 'sticky',
}

/** TH グループスタイル (token raw hex で生成) */
export function thGroupStyles(
  colorTextMid: string,
  colorBorderLow: string,
): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '6px 6px',
    // 8080 `.table-48 thead tr.sub-row th` = `--font-size-md` (14px) / 500
    // (Chromium 実測)。 13px / 600 はスケール外の生値だった
    // (デザイナー指摘 2026-08-28「TH を可能な限り DS 準拠」)。
    fontSize: fontSizes.md.value,
    fontWeight: 500,
    color: colorTextMid,
    background: LightModeSemanticColors.Surface.tableHeader.value,
    borderBottom: `2px solid ${colorBorderLow}`,
    borderRight: `1px solid ${colorBorderLow}`,
    position: 'sticky',
    top: 0,
    zIndex: 3,
  }
}
